import { isCarePlanServiceExecutionBlockerLabel } from '@/lib/care-plans/service-execution-contract';

export type CarePlanBlockerResolution = {
  label: string;
  hash?: string;
  actionLabel: string;
};

/** Plan-shape sections emitted by care_plan_readiness — edited on the plan record, not composer. */
export const CARE_PLAN_SHAPE_SECTIONS = [
  'restorative_episode',
  'advance_care_directive',
  'after_hours_escalation',
  'rac_respite_transition',
] as const;

export type CarePlanShapeSection = (typeof CARE_PLAN_SHAPE_SECTIONS)[number];

export const PLAN_SHAPE_ANCHOR = 'plan-shape';

const PLAN_SHAPE_BLOCKER_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  section: CarePlanShapeSection;
  actionLabel: string;
}> = [
  {
    pattern: /restorative episode start and expected end/i,
    section: 'restorative_episode',
    actionLabel: 'Edit restorative episode',
  },
  {
    pattern: /restorative funded episode duration/i,
    section: 'restorative_episode',
    actionLabel: 'Edit restorative episode',
  },
  {
    pattern: /restorative pathway access/i,
    section: 'restorative_episode',
    actionLabel: 'Edit restorative episode',
  },
  {
    pattern: /restorative exit assessment/i,
    section: 'restorative_episode',
    actionLabel: 'Edit restorative episode',
  },
  {
    pattern:
      /advance care directive|linked palliative plan reference|acd or linked acp_palliative/i,
    section: 'advance_care_directive',
    actionLabel: 'Edit advance care directive',
  },
  {
    pattern: /after-hours escalation/i,
    section: 'after_hours_escalation',
    actionLabel: 'Edit after-hours escalation',
  },
  {
    pattern: /rac respite.*transition|rac respite shape/i,
    section: 'rac_respite_transition',
    actionLabel: 'Edit respite transition',
  },
];

export function isCarePlanShapeSection(
  section: string | null | undefined,
): section is CarePlanShapeSection {
  return CARE_PLAN_SHAPE_SECTIONS.includes(section as CarePlanShapeSection);
}

export function isCarePlanAcknowledgementBlocker(blocker: string): boolean {
  const lower = blocker.trim().toLowerCase();
  if (lower.includes('service agreement') || lower.includes('agreement ledger')) return false;
  return (
    lower.includes('acknowledgement') ||
    lower.includes('acknowledgment') ||
    (lower.includes('representative') && lower.includes('care-plan'))
  );
}

export function carePlanShapeBlockerResolution(blocker: string): CarePlanBlockerResolution | null {
  const raw = blocker.trim();
  for (const entry of PLAN_SHAPE_BLOCKER_PATTERNS) {
    if (!entry.pattern.test(raw)) continue;
    return {
      label: raw,
      hash: PLAN_SHAPE_ANCHOR,
      actionLabel: entry.actionLabel,
    };
  }
  return null;
}

export function carePlanBlockerResolution(blocker: string): CarePlanBlockerResolution {
  const raw = blocker.trim();
  const lower = raw.toLowerCase();

  const shapeResolution = carePlanShapeBlockerResolution(raw);
  if (shapeResolution) return shapeResolution;

  // Per-service execution blockers (delivery model, goal link, schedule, worker
  // requirement, …) resolve on each inclusion row in the Services lane — never
  // on the terminal activate anchor, which has no editing surface for them.
  if (isCarePlanServiceExecutionBlockerLabel(raw)) {
    return {
      label: raw,
      hash: 'services',
      actionLabel: 'Open services',
    };
  }

  if (lower.includes('care-minute target') || lower.includes('care minute target')) {
    return {
      label: raw,
      hash: 'rac_care_minutes',
      actionLabel: 'Open care-minute targets',
    };
  }

  if (
    lower.includes('restrictive practice') ||
    lower.includes('behaviour support plan') ||
    lower.includes('bsp coverage')
  ) {
    return {
      label: raw,
      hash: 'restrictive_practice',
      actionLabel: 'Open restrictive practices',
    };
  }

  if (
    lower.includes('typed service inclusion') ||
    lower.includes('typed and date-valid') ||
    lower.includes('service inclusion') ||
    lower.includes('included services') ||
    lower.includes('services/schedule')
  ) {
    return {
      label:
        'Add one current service with its program, service type, start date, frequency, unit, and pricing.',
      hash: 'services',
      actionLabel: 'Open services',
    };
  }

  if (
    lower.includes('acknowledgement') ||
    lower.includes('acknowledgment') ||
    lower.includes('representative')
  ) {
    return {
      label:
        "Record the plan-review evidence required by this plan's provider template for the current version.",
      hash: 'composer-agree',
      actionLabel: 'Record plan review',
    };
  }

  if (lower.includes('service agreement') || lower.includes('agreement ledger')) {
    return {
      label:
        'Record the service agreement required for this plan and verify the participant or authorised decision-maker.',
      hash: 'composer-agree',
      actionLabel: 'Open agreement',
    };
  }

  if (
    lower.includes('budget') ||
    lower.includes('contribution basis') ||
    lower.includes('quarter')
  ) {
    return {
      label:
        'Resolve the budget or funding evidence that applies to this plan in Services & funding.',
      hash: 'budget',
      actionLabel: 'Open budget',
    };
  }

  if (lower.includes('care plan status does not allow approval request')) {
    return {
      label:
        'No approval request is needed for an active plan. Resolve the listed service, funding, and plan-review gaps instead.',
      hash: 'composer-activate',
      actionLabel: 'Open actions',
    };
  }

  if (lower.includes('at least one goal') || lower.includes('goal is required')) {
    return {
      label: raw,
      hash: 'goals',
      actionLabel: 'Open goals',
    };
  }

  if (lower.includes('at least one risk') || lower.includes('risk/control is required')) {
    return {
      label: raw,
      hash: 'risks',
      actionLabel: 'Open risks',
    };
  }

  if (
    lower.includes('at least one intervention') ||
    lower.includes('intervention/service instruction is required')
  ) {
    return {
      label: raw,
      hash: 'interventions',
      actionLabel: 'Open interventions',
    };
  }

  if (
    lower.includes('at least one preference') ||
    lower.includes('preference or cultural consideration is required')
  ) {
    return {
      label: raw,
      hash: 'preferences',
      actionLabel: 'Open preferences',
    };
  }

  if (lower.includes('worker instruction')) {
    return {
      label: raw,
      hash: 'worker_instructions',
      actionLabel: 'Open worker instructions',
    };
  }

  if (lower.includes('review date is required') || lower.includes('review date is set')) {
    return {
      label: 'Set the plan review date in Reviews / plan settings.',
      hash: 'reviews',
      actionLabel: 'Open review date',
    };
  }

  if (
    lower.includes('funding change') ||
    lower.includes('funding-change') ||
    lower.includes('payer assignment') ||
    lower.includes('payer cannot be changed') ||
    lower.includes('complete the funding change') ||
    lower.includes('complete the linked funding-change') ||
    lower.includes('governed funding-change') ||
    lower.includes('assign a current payer') ||
    lower.includes('apply the linked funding change')
  ) {
    return {
      label: raw,
      hash: 'services',
      actionLabel: 'Open funding change',
    };
  }

  if (
    lower.includes('template sections cannot be evaluated') ||
    lower.includes('care-plan template sections cannot be evaluated')
  ) {
    return {
      label:
        'This care-plan template is misconfigured — a coordinator or tenant admin must repair the template sections in Care plan template settings before readiness can be evaluated.',
      // No dedicated template-configuration anchor on the composer; keep the
      // action instructional rather than dumping the coordinator on activate.
      actionLabel: 'Review template settings',
    };
  }

  return { label: raw, hash: 'composer-activate', actionLabel: 'Open plan' };
}
